'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useAuthStore } from '@/stores/auth-store';
import { useOrgLabels } from '@/hooks/useOrgLabels';
import { useTenantTimezone } from '@/contexts/tenant-timezone-context';
import { useMedicationChartDate } from '@/hooks/useMedicationChartDate';
import { createClient } from '@/lib/supabase/client';
import { getAuthUserWithRetry } from '@/lib/supabase/auth-user';
import { useProgramNavigationHint } from '@/hooks/useProgramNavigationHint';
import { ProgramNavigationHintState } from './program-navigation-hint-state';

/** The shell chooser does not mount AuthGuard, so a hard reload has no store
 * profile. Verify this session's actor without publishing an auth profile or
 * reading a client. Auth events invalidate immediately; event payloads never
 * grant hint authority. The server independently proves actor AND tenant. */
function useHintSessionActor(actorId: string, tenantId: string) {
  const binding = JSON.stringify([actorId, tenantId]);
  const [proof, setProof] = useState<string | null>(null);
  useEffect(() => {
    let client: ReturnType<typeof createClient>;
    try {
      client = createClient();
    } catch {
      return;
    }
    let live = true;
    let generation = 0;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const verify = async () => {
      const attempt = ++generation;
      setProof(null);
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const result = await Promise.race([
          getAuthUserWithRetry(client, { retries: 0 }),
          new Promise<null>((resolve) => {
            timer = setTimeout(() => resolve(null), 5_000);
            timers.add(timer);
          }),
        ]);
        if (live && attempt === generation)
          setProof(!result?.error && result?.data?.user?.id === actorId ? binding : null);
      } catch {
        if (live && attempt === generation) setProof(null);
      } finally {
        if (timer !== undefined) {
          clearTimeout(timer);
          timers.delete(timer);
        }
      }
    };
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((event) => {
      generation++;
      setProof(null);
      if (event !== 'SIGNED_OUT')
        queueMicrotask(() => {
          if (live) void verify();
        });
    });
    void verify();
    return () => {
      live = false;
      generation++;
      subscription.unsubscribe();
      for (const timer of timers) clearTimeout(timer);
    };
  }, [actorId, tenantId, binding]);
  return proof === binding;
}

/** Does not admit children: fallback is always the existing refusal/chooser. */
function MountedClientProgramHintBoundary({
  actorId,
  tenantId,
  clientId,
  contextRevision,
  pathname,
  returnTo,
  timezone,
  standalone = false,
  children,
}: {
  actorId: string;
  tenantId: string;
  clientId: string;
  contextRevision: string;
  pathname: string;
  returnTo: string;
  timezone?: string | null;
  standalone?: boolean;
  children: ReactNode;
}) {
  const user = useAuthStore((state) => state.user);
  const sawUser = useRef(Boolean(user));
  if (user) sawUser.current = true;
  const currentPath = usePathname();
  const search = useSearchParams();
  const labels = useOrgLabels();
  const tenantTimezone = useTenantTimezone();
  const asOfDate = useMedicationChartDate(timezone ?? tenantTimezone ?? 'Australia/Sydney');
  const sessionMatches = useHintSessionActor(actorId, tenantId);
  const identityMatches =
    sessionMatches && (user ? user.id === actorId && user.tenantId === tenantId : !sawUser.current);
  const returnProgram = new URL(returnTo, 'https://local.invalid').searchParams.get('program');
  const eligible =
    identityMatches &&
    currentPath === pathname &&
    search.get('program') === returnProgram &&
    (!labels.isLoaded || labels.programContextRevision === contextRevision);
  const hint = useProgramNavigationHint(
    eligible
      ? {
          actorId,
          tenantId,
          clientId,
          contextRevision,
          pathname,
          asOfDate,
        }
      : null,
  );
  return hint ? (
    <ProgramNavigationHintState
      key={JSON.stringify([actorId, tenantId, clientId, contextRevision, asOfDate])}
      hint={hint}
      returnTo={returnTo}
      standalone={standalone}
      currentFamily={labels.isLoaded ? labels.activeProgramFamily : null}
    />
  ) : (
    children
  );
}

/** Keep the original server-rendered refusal until the client can bind the
 * optional request to live navigation/identity. No hint request during SSR. */
export function ClientProgramHintBoundary(
  props: Parameters<typeof MountedClientProgramHintBoundary>[0],
) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return mounted ? <MountedClientProgramHintBoundary {...props} /> : props.children;
}

/** For the already mounted medication page, use its actual actor and URL. */
export function CurrentClientProgramHintBoundary({
  clientId,
  contextRevision,
  children,
}: {
  clientId: string;
  contextRevision: string;
  children: ReactNode;
}) {
  const actor = useAuthStore((state) => state.user);
  const pathname = usePathname();
  const search = useSearchParams();
  if (!actor?.id || !actor.tenantId || !pathname) return children;
  return (
    <ClientProgramHintBoundary
      actorId={actor.id}
      tenantId={actor.tenantId}
      clientId={clientId}
      contextRevision={contextRevision}
      pathname={pathname}
      returnTo={`${pathname}${search.size ? `?${search.toString()}` : ''}`}
    >
      {children}
    </ClientProgramHintBoundary>
  );
}
